'use client'

import * as React from 'react'
import { useCallback, useState } from 'react'

import { Button } from './button'
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './dialog'
import { LoadingButton } from './loading-button'

interface FormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  /** Label for the submit button (default: "Save") */
  submitLabel?: string
  /** Disable the submit button (e.g. form validation) */
  submitDisabled?: boolean
  /** Async submit handler — errors are caught and displayed automatically */
  onSubmit: () => Promise<void>
  /** Called when the dialog closes (use to reset form state) */
  onReset?: () => void
  /** Show a secondary Cancel action; dialogs already provide a close control. */
  showCancel?: boolean
  /** DialogContent width class (default: "sm:max-w-[500px]") */
  width?: string
  /** Form content rendered inside DialogBody */
  children: React.ReactNode
}

function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel = 'Save',
  submitDisabled,
  onSubmit,
  onReset,
  width = 'sm:max-w-[500px]',
  showCancel = true,
  children,
}: FormDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleClose = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setError(null)
        onReset?.()
      }
      onOpenChange(newOpen)
    },
    [onOpenChange, onReset],
  )

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      await onSubmit()
      handleClose(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setIsSubmitting(false)
    }
  }, [onSubmit, handleClose])

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={width}
        primaryActionOnEnter
        {...(!description ? { 'aria-describedby': undefined } : {})}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <DialogBody className="space-y-4">
          {error && <p className="text-sm text-destructive">{error}</p>}
          {children}
        </DialogBody>

        <DialogFooter>
          {showCancel && (
            <Button variant="outline" onClick={() => handleClose(false)} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <LoadingButton
            onClick={handleSubmit}
            disabled={submitDisabled}
            loading={isSubmitting}
            data-dialog-primary-action="true">
            {submitLabel}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export { FormDialog, type FormDialogProps }
